<template>
  <div class="content-ownership-show-route">
    <div class="loadingcontainer container" v-if="isLoading">
      <ContentBlock :loading="isLoading" />
      <ContentBlock :loading="isLoading" />
    </div>
    <div class="loaded animated fadeIn" v-if="!isLoading">
      <SubHeader>
        <div slot="tabs">
          <ul>
            <RouterLink
              tag="li"
              :class="{ dot: !basicValidity || !playerValidity }"
              :to="{ name: 'tab-content' }"
            >{{ $t('Content Details') }}</RouterLink>
            <RouterLink
              v-if="!$permissions['enterprise-developer']"
              tag="li"
              :class="{ dot: !priceValidity }"
              :to="{ name: 'tab-pricing' }"
            >{{ $t('Pricing & Distribution') }}</RouterLink>
            <RouterLink
              v-if="!$permissions['enterprise-developer']"
              tag="li"
              :class="{ dot: !EULAValidity }"
              :to="{ name: 'tab-eula' }"
            >{{ $t('End User License Agreement') }}</RouterLink>
            <RouterLink
              tag="li"
              :class="{ dot: !CDSValidity }"
              :to="{ name: 'tab-cds' }"
            >{{ $t('Content Distribution Upload') }}</RouterLink>
            <RouterLink
              v-if="!contentOwnership.cds"
              tag="li"
              :class="{ dot: !keysValidity }"
              :to="{ name: 'tab-steam' }"
            >{{ $t('Steam Connect') }}</RouterLink>
            <RouterLink tag="li" :to="{ name: 'tab-update-notes' }">{{ $t('Update Notes') }}</RouterLink>
          </ul>
        </div>
      </SubHeader>
      <div class="container">
        <BreadcrumbNav :items="breadcrumb" />
        <RouterView :contentOwnership="contentOwnership" />
      </div>
      <BottomBar :valid="valid" @save="doSave" @cancel="doCancel" />
    </div>
  </div>
</template>

<script>
import { watchEffect, computed } from '@vue/composition-api';
import SubHeader from '@/core/shared/components/SubHeader';
import ContentBlock from '@/core/shared/components/ContentBlock';
import FocusModeMixin from '@/core/shared/misc/FocusModeMixin';
import BottomBar from '@/core/shared/components/BottomBar';
import BreadcrumbNav from '@/core/shared/components/BreadcrumbNav';
import ContentOwnershipValidatorHelper from '../ContentOwnershipValidatorHelper';
import { useObjectWithChangeDetection, useLoading } from '@/core/shared/composition-helpers';
import i18n from '@/core/i18n';

import store from '@/core/store';
import router from '@/core/routes';

export default {
  name: 'ContentOwnershipShowRoute',
  mixins: [FocusModeMixin],
  components: {
    SubHeader,
    ContentBlock,
    BottomBar,
    BreadcrumbNav,
  },
  props: {
    contentOwnershipId: {
      type: String,
      required: true,
    },
  },
  setup (props) {
    const contentOwnershipInStore = computed(() => {
      return store.getters['entities/ContentOwnership'](props.contentOwnershipId);
    });
    const { changes: contentOwnershipChanges, object: contentOwnership } = useObjectWithChangeDetection(contentOwnershipInStore);
    watchEffect(() => {
      contentOwnership.value?.title && store.commit('setTitle', contentOwnership.value.title);
    });

    const { loading: isLoading, subscribeToPromise } = useLoading();

    (function init () {
      const promise = store.dispatch('getContentOwnership', props.contentOwnershipId);
      subscribeToPromise(promise);
    })();

    const breadcrumb = computed(() => {
      return [
        {
          name: i18n.t('Content'),
          routerLink: { name: 'content-ownerships' },
        },
        {
          name: contentOwnership.value.title,
        },
      ];
    });

    const valid = computed(() => { return ContentOwnershipValidatorHelper.valid(contentOwnership.value); });
    const basicValidity = computed(() => { return ContentOwnershipValidatorHelper.basicValidity(contentOwnership.value); });
    const playerValidity = computed(() => { return ContentOwnershipValidatorHelper.playerValidity(contentOwnership.value); });
    const priceValidity = computed(() => { return ContentOwnershipValidatorHelper.priceValidity(contentOwnership.value); });
    const EULAValidity = computed(() => { return ContentOwnershipValidatorHelper.EULAValidity(contentOwnership.value); });
    const keysValidity = computed(() => { return ContentOwnershipValidatorHelper.keysValidity(contentOwnership.value); });
    const CDSValidity = computed(() => { return ContentOwnershipValidatorHelper.CDSValidity(contentOwnership.value); });

    function doCancel () {
      store.dispatch('removePendingKeys', { id: props.contentOwnershipId });
      router.push({ name: 'content-ownerships' });
    }

    async function doSave () {
      store.commit('setLoadingOverlay', true);

      store.commit('setFlash', {
        type: 'info',
        message: i18n.t('Saving...'),
      });

      store.commit('contentOwnerships/set', {
        path: 'allowWebsocketUpdates',
        value: false,
      });

      const successMessage = i18n.t('Your content was successfully updated.');
      const errorMessage = i18n.t('There was an error updating your content, please try again.');
      const contentOwnershipToSave = { id: props.contentOwnershipId, ...contentOwnershipChanges.value };

      try {
        await Promise.all([
          store.dispatch('commitPendingKeys', { id: props.contentOwnershipId }),
          store.dispatch('createPrices', contentOwnership.value),
          store.dispatch('createContentOwnershipAgreement', contentOwnershipToSave),
        ]);

        await store.dispatch('storeContentOwnership', contentOwnershipToSave);

        store.commit('removeFlash');

        store.commit('setFlash', {
          type: 'success',
          message: successMessage,
        });

        setTimeout(() => { store.commit('removeFlash'); }, 2000);
        router.push({ name: 'content-ownerships' });
      } catch (e) {
        store.commit('setFlash', {
          type: 'error',
          message: errorMessage,
        });
      } finally {
        store.commit('setLoadingOverlay', false);
        store.commit('contentOwnerships/set', {
          path: 'allowWebsocketUpdates',
          value: true,
        });
      }
    }
    return {
      contentOwnership,
      breadcrumb,
      isLoading,
      doCancel,
      doSave,
      valid,
      basicValidity,
      playerValidity,
      priceValidity,
      EULAValidity,
      keysValidity,
      CDSValidity,
    };
  },
};
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.dot {
  position: relative;

  &::after {
    display: block;
    content: "";
    top: 2rem;
    right: calc(var(--spacingMd) - 1.25rem);
    border-radius: 100%;
    position: absolute;
    width: 0.75rem;
    height: 0.65rem;
    background-color: var(--colorSBRedFlat);
  }
}
</style>

<style lang="postcss">
@import "core/shared/styles";

.loadingcontainer {
  padding-top: var(--spacingMd);
}

.breadcrumb-nav {
  margin-bottom: var(--spacingSm);
}

.update-notification {
  border: 0.1rem solid var(--colorGeyser);
  padding: var(--spacingMd);
  margin-bottom: var(--spacingMd);

  & .f9 {
    margin-bottom: 1.5rem;
    color: var(--colorFjord);
  }

  & ul {
    list-style-type: disc;
    padding-left: var(--spacingMd);
    margin-top: var(--spacingSm);
  }

  & li {
    margin-bottom: var(--spacingSm);
  }

  & i {
    color: var(--colorManatee);
    font-style: italic;
  }
}
</style>
